import jwt from 'jsonwebtoken'
import { enqueueSnackbar } from "notistack"; // Directly use the function outside components

export const trimText = (text, count = 30) => {
  return text && text.length > count
    ? text.slice(0, count) + "..."
    : text || "";
};

export function copyTextToClipboard(text) {
  if (!navigator.clipboard) {
    console.error("Clipboard API not supported by this browser.");
    return;
  }

  navigator.clipboard
    .writeText(text)
    .then(() => {
      // toast.success("Copied!")
    })
    .catch((err) => {
      console.error("Failed to copy text: ", err);
    });
}

export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
export function currentMonthAndYear() {
  const currentDate = new Date();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const year = currentDate.getFullYear();
  return `${month}/${year}`;
}
export function formattedDate(dateString) {
  // Create a Date object from the string
  const date = new Date(dateString);

  // Extract year, month, and day
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed, so we add 1
  const day = String(date.getDate()).padStart(2, "0"); // Ensures two-digit day

  // Format as "YYYY-MM-DD"
  return `${year}-${month}-${day}`;
}


export function daysLeftUntilExpiration(isTrail, startDate, monthsToAdd = 1) {
  const expiryDate = new Date(startDate);
  if (isTrail) {
    expiryDate.setDate(expiryDate.getDate() + 14);
  } else {
    // Add the number of months for non-trial
    expiryDate.setMonth(expiryDate.getMonth() + monthsToAdd);
  }

  const currentDate = new Date(); // Get the current date
  const timeDifference = expiryDate - currentDate; // Difference in milliseconds

  // Convert time difference from milliseconds to days
  const daysRemaining = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  const result = daysRemaining - 1;

  return result < 0 ? 0 : result;
}

export function checkGoogleLogin(token) {
  try {
    const decoded = jwt.decode(token);

    return decoded?.isGoogleLogin || false
  } catch (error) {
    return false
  }
}

export function stripHtmlAndTrim(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || ""; // Ensure it returns an empty string if no content is found
};

export function capitalizeFirstLetter(string) {
  if (!string) return '---'; // Return default if string is null/undefined
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const showNotification = (message) => {
  const storedLanguage = localStorage.getItem("language");
  const mess = storedLanguage == 'en' ? 'Site under Maintenance' : 'Website wird gewartet';

  enqueueSnackbar(message ? message : mess, {
    variant: "error",
    autoHideDuration: 2000,
    preventDuplicate: true
  });
};
const findDifferenceIndexes = (text1, text2) => {
  let startIdx = 0;
  let endIdx = 0;

  // Find the starting index where the difference begins
  while (startIdx < text1.length && startIdx < text2.length && text1[startIdx] === text2[startIdx]) {
    startIdx++;
  }

  // Find the ending index where the difference ends
  endIdx = startIdx;
  while (endIdx < text1.length && endIdx < text2.length && text1[endIdx] !== text2[endIdx]) {
    endIdx++;
  }

  return { startIdx, endIdx };
};

// Highlighting only the differing portion of two strings
export const highlightDiff = (text1, text2) => {
  const { startIdx, endIdx } = findDifferenceIndexes(text1, text2);

  // If there's a difference, highlight the differing part
  if (startIdx < text1.length && endIdx > startIdx) {
    return (
      <>
        {/* Display the part before the difference */}
        <span>{text1.slice(0, startIdx)}</span>

        {/* Highlight only the differing part */}
        <span style={{ backgroundColor: "yellow" }}>
          {text1.slice(startIdx, endIdx)}
        </span>

        {/* Display the part after the difference */}
        <span>{text1.slice(endIdx)}</span>
      </>
    );
  }

  // If no difference, return the text as it is
  return <span>{text1}</span>;
};

export const hasScrapedAndSpapiBp = (product) => {

  const hasScrappedBulletPoints = Object.keys(product)
    .filter(key => key.startsWith("bulletpoint_de_") && key.endsWith("_scrapped"))
    .some(key => product[key] !== null && product[key].trim() !== "");

  const allApiBulletPointsNull = Object.keys(product)
    .filter(key => key.startsWith("bulletpoint_de_") && key.endsWith("_api"))
    .every(key => product[key] === null);

  return allApiBulletPointsNull && hasScrappedBulletPoints;
}

export const localizationMessage = (message, keys = null) => {
  const translations = {
    "Already loaded your products Remaining-time:": "Ihre Produkte wurden bereits geladen. Verbleibende Zeit: ",
    "minutes": "Minuten",
    "loading your products in progress":"Ihre Produkte werden geladen"
  };

  let localizedMessage =  message || "";

  // Replace translations dynamically
  Object.keys(translations).forEach(key => {
    localizedMessage = localizedMessage.replace(key, translations[key]);
  });

  return localizedMessage;
}

/**
 * Optimizes Amazon image URLs to request appropriate sizes for faster loading.
 * Amazon image URLs contain size suffixes like _SL1500_ which can be modified.
 * 
 * @param {string} url - The Amazon image URL
 * @param {number} displaySize - The size (px) at which the image will be displayed
 * @param {number} pixelRatio - Device pixel ratio for retina displays (default 2)
 * @returns {string} - Optimized URL with appropriate size suffix
 * 
 * Available Amazon sizes: 75, 160, 200, 250, 320, 500, 1000, 1500
 * 
 * Example:
 * Input:  https://m.media-amazon.com/images/I/61aE+62tMlL._AC_SL1500_.jpg
 * Output: https://m.media-amazon.com/images/I/61aE+62tMlL._AC_SL320_.jpg (for 160px display)
 */
export function optimizeAmazonImageUrl(url, displaySize = 160, pixelRatio = 2) {
  if (!url || typeof url !== 'string') return url;
  
  // Only process Amazon media URLs
  if (!url.includes('m.media-amazon.com') && !url.includes('images-amazon.com')) {
    return url;
  }
  
  // Calculate the ideal size (display size * pixel ratio for retina)
  const idealSize = displaySize * pixelRatio;
  
  // Available Amazon image sizes
  const availableSizes = [75, 160, 200, 250, 320, 500, 1000, 1500];
  
  // Find the smallest size that's >= idealSize
  const optimalSize = availableSizes.find(size => size >= idealSize) || availableSizes[availableSizes.length - 1];
  
  // Pattern to match Amazon size suffixes like _SL1500_, _AC_SL1500_, _SS1500_, etc.
  // Common patterns: _SL{size}_, _SS{size}_, _AC_SL{size}_, _AC_SS{size}_
  const sizePattern = /(_(?:AC_)?S[LS])(\d+)(_)/g;
  
  // Replace with optimal size
  const optimizedUrl = url.replace(sizePattern, `$1${optimalSize}$3`);
  
  return optimizedUrl;
}